// The widget palette — the prototype's slide-out drawer off a fixed left rail
// (#hover-zone + #side-panel). Hovering the 56px rail slides the drawer out; it
// holds a module search + the sections as icon tiles. Pointer-down on a tile
// starts a palette drag (handled by V2App) and closes the drawer (you're dragging
// a tile onto the canvas). A short close delay keeps it open while the pointer
// crosses from the rail to the drawer.

import { useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { WIDGET_DEFS, type WidgetDef } from "../registry.js";
import { theme } from "../theme.js";

const SECTIONS: { title: string; keys: string[] }[] = [
  { title: "Player", keys: ["player-bio", "appearance", "matches"] },
  { title: "Player Stats", keys: ["passing", "attacking", "defending", "goalkeeping"] },
];

// A 20px Feather-style glyph per widget (the prototype's menu-icons for the
// Player group; simple distinct marks for the stats group).
const ICONS: Record<string, React.ReactNode> = {
  "player-bio": (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    </>
  ),
  appearance: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" />
      <rect x="12" y="8" width="3" height="10" />
      <rect x="17" y="5" width="3" height="13" />
    </>
  ),
  matches: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <rect x="3" y="10" width="12" height="4" rx="1" />
      <rect x="3" y="16" width="15" height="4" rx="1" />
    </>
  ),
  passing: (
    <>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </>
  ),
  attacking: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  defending: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  goalkeeping: (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
};

function Glyph({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICONS[name] ?? <rect x="4" y="4" width="16" height="16" rx="2" />}
    </svg>
  );
}

export default function Palette({
  onTilePointerDown,
}: {
  onTilePointerDown: (e: ReactPointerEvent, def: WidgetDef) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // A short delay on leave so moving the pointer from the rail to the drawer (or
  // between drawer children) doesn't flicker it shut.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const q = query.trim().toLowerCase();
  const sections = useMemo(
    () =>
      SECTIONS.map((s) => ({
        title: s.title,
        defs: s.keys
          .map((k) => WIDGET_DEFS.find((d) => d.key === k))
          .filter((d): d is WidgetDef => Boolean(d))
          .filter((d) => (q ? d.title.toLowerCase().includes(q) : true)),
      })).filter((s) => s.defs.length > 0),
    [q]
  );

  return (
    <div style={{ position: "relative", flexShrink: 0, height: "100%", zIndex: 41 }}>
      {/* The 56px rail — always visible; hover it to open the drawer. */}
      <div
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        style={{
          width: 56,
          height: "100%",
          background: theme.tileHeader,
          borderRight: `1px solid ${theme.borderSubtle}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 16,
          boxSizing: "border-box",
          cursor: "pointer",
          position: "relative",
          zIndex: 2,
        }}
        aria-label="Open widget menu"
      >
        <span style={{ color: theme.inkMuted, display: "flex" }}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </span>
      </div>

      {/* The drawer — slides out from behind the rail (z below it). */}
      <div
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        style={{
          position: "absolute",
          top: 0,
          left: 56,
          height: "100%",
          width: 300,
          boxSizing: "border-box",
          background: theme.tileHeader,
          borderRight: `1px solid ${theme.border}`,
          boxShadow: open ? `5px 0 15px ${theme.shadowMd}` : "none",
          padding: "16px 14px",
          overflowY: "auto",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1,
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search modules…"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 14,
            padding: "7px 10px",
            background: theme.field,
            color: theme.ink,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            fontFamily: theme.fontBody,
            fontSize: theme.fontSize.data,
            outline: "none",
          }}
        />

        {sections.length === 0 ? (
          <div style={{ color: theme.inkFaint, fontSize: theme.fontSize.data }}>No modules match.</div>
        ) : (
          sections.map((section) => (
            <div key={section.title} style={{ marginBottom: 16 }}>
              <div
                style={{
                  color: theme.inkFaint,
                  fontFamily: theme.fontBody,
                  fontSize: theme.fontSize.label,
                  fontWeight: 600,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                {section.title}
              </div>
              {/* Two-column grid of icon tiles (the prototype's .menu-grid). */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {section.defs.map((def) => (
                  <div
                    key={def.key}
                    onPointerDown={(e) => {
                      // Dragging a tile out: close the drawer so it doesn't cover
                      // the drop, then hand the drag to V2App.
                      setOpen(false);
                      onTilePointerDown(e, def);
                    }}
                    title={`Drag ${def.title} onto the canvas`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "14px 8px",
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      color: theme.inkMuted,
                      fontSize: theme.fontSize.data,
                      textAlign: "center",
                      cursor: "grab",
                      userSelect: "none",
                      touchAction: "none",
                    }}
                  >
                    <Glyph name={def.key} />
                    {def.title}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
